import { orderBy } from "@progress/kendo-data-query";
import { Grid, GridColumn, GridPageChangeEvent, GridRowClickEvent, GridSortChangeEvent } from "@progress/kendo-react-grid";
import {
    RadioButton,
    RadioButtonChangeEvent,
} from "@progress/kendo-react-inputs";
import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ErrorMessage from "../Common/Components/ErrorMessage";
import Spinner from "../Common/Components/spinner";
import { getOptionsSelector } from "../Common/Slices/optionSlice";
import { getParamsSelector, updateParams } from "../Common/Slices/ParamSlice";
import { machineTimesToHours } from "../Common/Util_methods/format";
import { paramsById } from "../Common/Util_methods/scope";
import { useAppDispatch } from "../Core/store.hooks";
import { getDevicesSelector } from "../Devices/Slice";
import { getMachineTimesLoadingSelector, getMachineTimesSelector } from "./slice";

interface GridWidgetProps {
    id: string;
    header: string;
}

interface PageState {
    skip: number;
    take: number;
}

const durationFields = {
    failure: "failureDuration",
    failureTimeout: "failureTimeoutDuration",
    idleAfterFailure: "idleAfterFailureDuration",
    idleAfterRunning: "idleAfterRunningDuration",
    running: "runningDuration",
    shift: "shiftDuration"
}

const durationFieldsPerDay = {
    failure: "failureDurationPerDay",
    failureTimeout: "failureTimeoutDurationPerDay",
    idleAfterFailure: "idleAfterFailureDurationPerDay",
    idleAfterRunning: "idleAfterRunningDurationPerDay",
    running: "runningDurationPerDay",
    shift: "shiftDurationPerDay"
}

const MachineTimesGridWidget = (props: GridWidgetProps) => {
    const initialSort: any = [{
        field: "failureDuration",
        dir: "desc",
    },];

    const initialDataState: PageState = { skip: 0, take: 20 };

    const [page, setPage] = useState<PageState>(initialDataState);
    const [sort, setSort] = useState(initialSort);
    const [selectedValues, setSelectedValues] = useState(durationFields);
    const [gridData, updateGridData] = useState([]);
    const [dataProcessed, setDataProcessed] = useState(false);

    const machineTimesData = useSelector(getMachineTimesSelector);
    const loadingState = useSelector(getMachineTimesLoadingSelector);
    const options = useSelector(getOptionsSelector);
    const params = useSelector(getParamsSelector);
    const deviceObjects = useSelector(getDevicesSelector);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (machineTimesData.length > 0) {
            updateGridData(machineTimesToHours(machineTimesData))
            setPage(initialDataState)
            setDataProcessed(true)
        }
    }, [machineTimesData])

    useEffect(()=>{
        
        if(loadingState === "pending"){
            setDataProcessed(false);
        }
        if(loadingState === "rejected"){
            setDataProcessed(true);
        }
        if(loadingState === "fulfilled" && machineTimesData.length === 0){
            setDataProcessed(true);
        }
    },[loadingState])

    const pageChange = (event: GridPageChangeEvent) => {
        setPage(event.page);
    };

    const handleChange = (e: RadioButtonChangeEvent) => {
        setSelectedValues(e.value);
    }

    const handleRowClick = (id: string) => {
        const newParams = paramsById(params, options, deviceObjects, id)
        if (newParams !== null) {
            dispatch(updateParams(newParams))
        }
    }

    if (loadingState === "pending") return <Spinner text="data" />;
    if (machineTimesData == null || loadingState === "rejected") {
        //proper error handling should be added with promise error response
        return (
            <ErrorMessage
                text={"Oops something went wrong, unable to get data."}
                id={props.id}
                header={props.header}
            />
        );
    }
    if (machineTimesData != null && machineTimesData.length == 0) {
        return (
            <ErrorMessage
                text={"No data found for selected filters."}
                id={props.id}
                header={props.header}
            />
        );
    }

    return (
        <div style={{height:"calc(100% - 30px)"} }>
            <div style={{ "margin": 10 }}>
                <RadioButton
                    name="group1"
                    value={durationFields}
                    checked={selectedValues === durationFields}
                    label="Total Times"
                    onChange={handleChange}
                />
                <RadioButton
                    name="group1"
                    value={durationFieldsPerDay}
                    checked={selectedValues === durationFieldsPerDay}
                    label="Daily Average"
                    onChange={handleChange}
                />
            </div>
            <div>Unit: Hours</div>
            <Grid
                data={orderBy(gridData, sort).slice(page.skip, page.take + page.skip)}
                sortable={true}
                sort={sort}
                onSortChange={(e: GridSortChangeEvent) => {
                    setSort(e.sort);
                }}
                columnVirtualization={true}
                pageSize={20}
                scrollable={"virtual"}
               // pageable={true}
                onPageChange={pageChange}
                skip={page.skip}
                take={page.take}
                total={gridData.length}
                onRowClick={(event: GridRowClickEvent) => handleRowClick(event.dataItem.id)}
                style={{ fontSize: "13px", height: " 100%" }}
                resizable={true}
                
            >
                <GridColumn field="text" title=" " width="130px"/>
                <GridColumn field={selectedValues.running} title="Running" width="100px" />
                <GridColumn field={selectedValues.failure} title="Failure" width="100px" />
                <GridColumn field={selectedValues.failureTimeout} title="Failure Timeout" width="100px" />
                <GridColumn field={selectedValues.idleAfterRunning} title="Idle After Running" width="90px" />
                <GridColumn field={selectedValues.idleAfterFailure} title="Idle After Failure" width="75px" />
                <GridColumn field={selectedValues.shift} title="Shift" width="75px"  />
            </Grid>
        </div>
    )
}

export default MachineTimesGridWidget;